import { supabase } from './supabase';

// Type definitions for credit analytics
export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  transaction_type: 'deduction' | 'addition' | 'purchase' | 'subscription_renewal' | 'referral_bonus' | 'refund';
  reason: string;
  metadata: Record<string, any>;
  related_analysis_id?: string;
  related_subscription_id?: string;
  related_payment_intent_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreditUsageHistory {
  transactions: CreditTransaction[];
  totalCreditsUsed: number;
  totalCreditsAdded: number;
  netCreditChange: number;
  periodStart: string;
  periodEnd: string;
}

export interface CreditEfficiencyMetrics {
  averageCreditsPerAnalysis: number;
  mostEfficientPlatform: string;
  leastEfficientPlatform: string;
  efficiencyScore: number; // 0-100 scale
  optimizationPotential: number; // Percentage improvement possible
  monthlyTrends: Array<{
    month: string;
    efficiency: number;
    creditsUsed: number;
    analysesPerformed: number;
  }>;
}

export interface PlatformUsageBreakdown {
  platform: string;
  creditsUsed: number;
  analysesCount: number;
  averageScore: number;
  successRate: number;
  lastUsed: string;
  trend: 'increasing' | 'decreasing' | 'stable';
}

export interface CreditOptimizationSuggestion {
  type: 'platform_focus' | 'timing_optimization' | 'batch_analysis' | 'plan_upgrade';
  title: string;
  description: string;
  potentialSavings: number; // Credits per month
  priority: 'high' | 'medium' | 'low';
  actionRequired: string;
}

export interface CreditUsageAnalytics {
  userId: string;
  currentBalance: number;
  usageHistory: CreditUsageHistory;
  efficiencyMetrics: CreditEfficiencyMetrics;
  platformBreakdown: PlatformUsageBreakdown[];
  optimizationSuggestions: CreditOptimizationSuggestion[];
  projectedRunoutDate?: string;
  recommendedTopupAmount?: number;
}

export class CreditAnalyticsService {
  
  /**
   * Get comprehensive credit usage history for a user
   */
  static async getCreditUsageHistory(
    userId?: string,
    periodDays: number = 30
  ): Promise<CreditUsageHistory> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const targetUserId = userId || user.id;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - periodDays);

      const { data: transactions, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', targetUserId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const creditTransactions = (transactions || []) as CreditTransaction[];
      const totalCreditsUsed = creditTransactions
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      const totalCreditsAdded = creditTransactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);

      return {
        transactions: creditTransactions,
        totalCreditsUsed,
        totalCreditsAdded,
        netCreditChange: totalCreditsAdded - totalCreditsUsed,
        periodStart: startDate.toISOString(),
        periodEnd: new Date().toISOString()
      };
    } catch (error) {
      console.error('Get credit usage history error:', error);
      throw error;
    }
  }

  /**
   * Get credit efficiency metrics for optimization
   */
  static async getCreditEfficiencyMetrics(userId?: string): Promise<CreditEfficiencyMetrics> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const targetUserId = userId || user.id;

      // Get last 6 months of analytics data
      const { data: analyticsData, error: analyticsError } = await supabase
        .from('credit_usage_analytics')
        .select('*')
        .eq('user_id', targetUserId)
        .gte('analysis_date', new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('analysis_date', { ascending: false });

      if (analyticsError) {
        throw analyticsError;
      }

      const analytics = analyticsData || [];
      
      // Calculate efficiency metrics
      const totalCreditsUsed = analytics.reduce((sum, a) => sum + (a.credits_used || 0), 0);
      const totalAnalyses = analytics.reduce((sum, a) => sum + (a.analyses_performed || 0), 0);
      const averageCreditsPerAnalysis = totalAnalyses > 0 ? totalCreditsUsed / totalAnalyses : 0;

      // Platform efficiency analysis
      const platformStats = new Map<string, { credits: number; analyses: number; scores: number[] }>();
      
      analytics.forEach(record => {
        if (record.platforms_analyzed && record.most_used_platform) {
          const platform = record.most_used_platform;
          const current = platformStats.get(platform) || { credits: 0, analyses: 0, scores: [] };
          current.credits += record.credits_used || 0;
          current.analyses += record.analyses_performed || 0;
          if (record.avg_analysis_score) {
            current.scores.push(record.avg_analysis_score);
          }
          platformStats.set(platform, current);
        }
      });

      // Find most and least efficient platforms
      let mostEfficient = '';
      let leastEfficient = '';
      let bestRatio = Infinity;
      let worstRatio = 0;

      for (const [platform, stats] of platformStats) {
        if (stats.analyses > 0) {
          const ratio = stats.credits / stats.analyses;
          if (ratio < bestRatio) {
            bestRatio = ratio;
            mostEfficient = platform;
          }
          if (ratio > worstRatio) {
            worstRatio = ratio;
            leastEfficient = platform;
          }
        }
      }

      // Calculate overall efficiency score (0-100)
      const baselineCreditsPerAnalysis = 1; // Our target efficiency
      const efficiencyScore = Math.max(0, Math.min(100, 
        100 - ((averageCreditsPerAnalysis - baselineCreditsPerAnalysis) / baselineCreditsPerAnalysis * 100)
      ));

      // Calculate optimization potential
      const optimizationPotential = Math.max(0, 
        ((averageCreditsPerAnalysis - baselineCreditsPerAnalysis) / averageCreditsPerAnalysis) * 100
      );

      // Monthly trends
      const monthlyTrends = analytics.slice(0, 6).map(record => ({
        month: new Date(record.analysis_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        efficiency: record.efficiency_score || 0,
        creditsUsed: record.credits_used || 0,
        analysesPerformed: record.analyses_performed || 0
      }));

      return {
        averageCreditsPerAnalysis,
        mostEfficientPlatform: mostEfficient || 'N/A',
        leastEfficientPlatform: leastEfficient || 'N/A',
        efficiencyScore,
        optimizationPotential,
        monthlyTrends
      };
    } catch (error) {
      console.error('Get credit efficiency metrics error:', error);
      throw error;
    }
  }

  /**
   * Get platform-specific usage analytics
   */
  static async getPlatformSpecificUsage(userId?: string): Promise<PlatformUsageBreakdown[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const targetUserId = userId || user.id;

      // Get user's creatives and analyses
      const { data: creatives, error: creativesError } = await supabase
        .from('creatives')
        .select('id')
        .eq('user_id', targetUserId);

      if (creativesError || !creatives) {
        return [];
      }

      const creativeIds = creatives.map(c => c.id);
      if (creativeIds.length === 0) {
        return [];
      }

      // Get analyses for platform breakdown
      const { data: analyses, error: analysesError } = await supabase
        .from('analyses')
        .select('platforms, overall_score, created_at')
        .in('creative_id', creativeIds)
        .order('created_at', { ascending: false });

      if (analysesError || !analyses) {
        return [];
      }

      // Process platform statistics
      const platformStats = new Map<string, {
        creditsUsed: number;
        analysesCount: number;
        scores: number[];
        dates: string[];
      }>();

      analyses.forEach(analysis => {
        const platforms = analysis.platforms || [];
        const score = analysis.overall_score || 0;
        const date = analysis.created_at || '';

        platforms.forEach(platform => {
          const current = platformStats.get(platform) || {
            creditsUsed: 0,
            analysesCount: 0,
            scores: [],
            dates: []
          };
          
          current.creditsUsed += 1; // Each analysis costs 1 credit
          current.analysesCount += 1;
          if (score > 0) current.scores.push(score);
          current.dates.push(date);
          
          platformStats.set(platform, current);
        });
      });

      // Convert to breakdown format
      const platformBreakdown: PlatformUsageBreakdown[] = [];

      for (const [platform, stats] of platformStats) {
        const averageScore = stats.scores.length > 0 
          ? stats.scores.reduce((sum, score) => sum + score, 0) / stats.scores.length 
          : 0;
        
        const successRate = stats.scores.length > 0 
          ? (stats.scores.filter(score => score >= 70).length / stats.scores.length) * 100 
          : 0;

        const lastUsed = stats.dates.length > 0 
          ? stats.dates.sort().reverse()[0] 
          : '';

        // Calculate trend (simple: comparing recent vs older usage)
        const recentAnalyses = stats.dates.filter(date => 
          new Date(date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        ).length;
        const olderAnalyses = stats.analysesCount - recentAnalyses;
        
        let trend: 'increasing' | 'decreasing' | 'stable' = 'stable';
        if (olderAnalyses > 0) {
          const ratio = recentAnalyses / olderAnalyses;
          if (ratio > 1.2) trend = 'increasing';
          else if (ratio < 0.8) trend = 'decreasing';
        }

        platformBreakdown.push({
          platform,
          creditsUsed: stats.creditsUsed,
          analysesCount: stats.analysesCount,
          averageScore,
          successRate,
          lastUsed,
          trend
        });
      }

      return platformBreakdown.sort((a, b) => b.creditsUsed - a.creditsUsed);
    } catch (error) {
      console.error('Get platform specific usage error:', error);
      throw error;
    }
  }

  /**
   * Get credit optimization suggestions
   */
  static async getCreditOptimizationSuggestions(userId?: string): Promise<CreditOptimizationSuggestion[]> {
    try {
      const [usageHistory, efficiencyMetrics, platformBreakdown] = await Promise.all([
        this.getCreditUsageHistory(userId, 30),
        this.getCreditEfficiencyMetrics(userId),
        this.getPlatformSpecificUsage(userId)
      ]);

      const suggestions: CreditOptimizationSuggestion[] = [];

      // Platform focus suggestion
      if (platformBreakdown.length > 1) {
        const bestPlatform = platformBreakdown.reduce((best, current) => 
          current.successRate > best.successRate ? current : best
        );
        
        if (bestPlatform.successRate > 80) {
          suggestions.push({
            type: 'platform_focus',
            title: `Focus on ${bestPlatform.platform}`,
            description: `${bestPlatform.platform} shows the highest success rate (${bestPlatform.successRate.toFixed(1)}%). Consider prioritizing this platform for better ROI.`,
            potentialSavings: Math.floor(usageHistory.totalCreditsUsed * 0.15),
            priority: 'medium',
            actionRequired: `Analyze more creatives on ${bestPlatform.platform} platform first`
          });
        }
      }

      // Efficiency improvement suggestion
      if (efficiencyMetrics.efficiencyScore < 70) {
        suggestions.push({
          type: 'timing_optimization',
          title: 'Improve Analysis Efficiency',
          description: `Your current efficiency score is ${efficiencyMetrics.efficiencyScore.toFixed(1)}%. Consider batching similar creatives for analysis.`,
          potentialSavings: Math.floor(usageHistory.totalCreditsUsed * (efficiencyMetrics.optimizationPotential / 100)),
          priority: 'high',
          actionRequired: 'Group similar creatives and analyze them together'
        });
      }

      // Batch analysis suggestion
      if (usageHistory.transactions.filter(t => t.transaction_type === 'deduction').length > 20) {
        suggestions.push({
          type: 'batch_analysis',
          title: 'Use Batch Analysis',
          description: 'You perform many individual analyses. Consider uploading multiple creatives at once to optimize credit usage.',
          potentialSavings: Math.floor(usageHistory.totalCreditsUsed * 0.1),
          priority: 'medium',
          actionRequired: 'Upload multiple creatives in a single session'
        });
      }

      // Plan upgrade suggestion
      const currentCredits = await this.getCurrentCreditBalance();
      if (currentCredits < 10 && usageHistory.totalCreditsUsed > 50) {
        suggestions.push({
          type: 'plan_upgrade',
          title: 'Consider Plan Upgrade',
          description: 'Your usage patterns suggest a higher plan would be more cost-effective.',
          potentialSavings: Math.floor(usageHistory.totalCreditsUsed * 0.2),
          priority: 'high',
          actionRequired: 'Review subscription plans for better value'
        });
      }

      return suggestions.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });
    } catch (error) {
      console.error('Get credit optimization suggestions error:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive credit analytics dashboard data
   */
  static async getCreditUsageAnalytics(userId?: string): Promise<CreditUsageAnalytics> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const targetUserId = userId || user.id;

      const [
        currentBalance,
        usageHistory,
        efficiencyMetrics,
        platformBreakdown,
        optimizationSuggestions
      ] = await Promise.all([
        this.getCurrentCreditBalance(),
        this.getCreditUsageHistory(targetUserId),
        this.getCreditEfficiencyMetrics(targetUserId),
        this.getPlatformSpecificUsage(targetUserId),
        this.getCreditOptimizationSuggestions(targetUserId)
      ]);

      // Calculate projected runout date
      let projectedRunoutDate: string | undefined;
      let recommendedTopupAmount: number | undefined;

      if (usageHistory.totalCreditsUsed > 0 && currentBalance > 0) {
        const dailyUsage = usageHistory.totalCreditsUsed / 30; // Last 30 days
        if (dailyUsage > 0) {
          const daysRemaining = currentBalance / dailyUsage;
          projectedRunoutDate = new Date(Date.now() + daysRemaining * 24 * 60 * 60 * 1000).toISOString();
          
          // Recommend topup amount based on usage patterns
          const monthlyUsage = dailyUsage * 30;
          recommendedTopupAmount = Math.ceil(monthlyUsage * 2); // 2 months worth
        }
      }

      return {
        userId: targetUserId,
        currentBalance,
        usageHistory,
        efficiencyMetrics,
        platformBreakdown,
        optimizationSuggestions,
        projectedRunoutDate,
        recommendedTopupAmount
      };
    } catch (error) {
      console.error('Get credit usage analytics error:', error);
      throw error;
    }
  }

  /**
   * Record credit transaction
   */
  static async recordCreditTransaction(
    amount: number,
    transactionType: CreditTransaction['transaction_type'],
    reason: string,
    metadata: Record<string, any> = {},
    relatedAnalysisId?: string,
    relatedSubscriptionId?: string,
    relatedPaymentIntentId?: string
  ): Promise<CreditTransaction> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const transactionData = {
        user_id: user.id,
        amount,
        transaction_type: transactionType,
        reason,
        metadata,
        related_analysis_id: relatedAnalysisId,
        related_subscription_id: relatedSubscriptionId,
        related_payment_intent_id: relatedPaymentIntentId
      };

      const { data, error } = await supabase
        .from('credit_transactions')
        .insert(transactionData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data as CreditTransaction;
    } catch (error) {
      console.error('Record credit transaction error:', error);
      throw error;
    }
  }

  /**
   * Update daily usage analytics
   */
  static async updateDailyUsageAnalytics(
    userId: string,
    analysisDate: string,
    creditsUsed: number,
    analysesPerformed: number,
    platformsAnalyzed: string[],
    avgAnalysisScore?: number
  ): Promise<void> {
    try {
      // Calculate efficiency score
      const baselineCreditsPerAnalysis = 1;
      const actualCreditsPerAnalysis = analysesPerformed > 0 ? creditsUsed / analysesPerformed : 0;
      const efficiencyScore = Math.max(0, Math.min(100, 
        100 - ((actualCreditsPerAnalysis - baselineCreditsPerAnalysis) / baselineCreditsPerAnalysis * 100)
      ));

      // Find most used platform
      const platformCount = new Map<string, number>();
      platformsAnalyzed.forEach(platform => {
        platformCount.set(platform, (platformCount.get(platform) || 0) + 1);
      });
      
      const mostUsedPlatform = Array.from(platformCount.entries())
        .sort((a, b) => b[1] - a[1])[0]?.[0] || null;

      const analyticsData = {
        user_id: userId,
        analysis_date: analysisDate,
        credits_used: creditsUsed,
        analyses_performed: analysesPerformed,
        platforms_analyzed: platformsAnalyzed,
        avg_analysis_score: avgAnalysisScore,
        most_used_platform: mostUsedPlatform,
        efficiency_score: efficiencyScore
      };

      const { error } = await supabase
        .from('credit_usage_analytics')
        .upsert(analyticsData, { 
          onConflict: 'user_id,analysis_date',
          ignoreDuplicates: false 
        });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Update daily usage analytics error:', error);
      throw error;
    }
  }

  /**
   * Get current credit balance (helper method)
   */
  private static async getCurrentCreditBalance(): Promise<number> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: userData, error } = await supabase
        .from('users')
        .select('credits')
        .eq('id', user.id)
        .single();

      if (error) {
        throw error;
      }

      return userData.credits || 0;
    } catch (error) {
      console.error('Get current credit balance error:', error);
      return 0;
    }
  }
} 